/**
 * Perfect-play solver for Vanishing XOX.
 *
 * The game has 128,170 reachable positions (ordered marks per side + side to move), so it is
 * solved completely at startup with a retrograde analysis and every move is then an O(9) lookup.
 * Positions are packed into integers and laid out in a dense slot space so the table is a single
 * Int8Array (~690 KB) and builds in a few dozen milliseconds.
 *
 * Because marks vanish, perfect play can cycle forever; those positions are "cycle" and the
 * computer mode declares a draw by repetition or a ply cap (see `isAiDraw`).
 */
import { seededRandom } from '../random'
import {
  applyMove,
  BOARD_SIZE,
  type Cell,
  createGame,
  type GameState,
  MAX_MARKS,
  other,
  WIN_LINES,
} from './engine'

// ---- packed side encoding -----------------------------------------------------------------
// A side's marks in placement order, oldest in the low nibble, each nibble = cell + 1 (0 = empty).

const SLOT_BITS = 4
const NIBBLE = (1 << SLOT_BITS) - 1
const PACK_SPACE = 1 << (SLOT_BITS * MAX_MARKS)

function packSide(cells: readonly Cell[]): number {
  let p = 0
  for (let i = 0; i < cells.length; i++) p |= (cells[i]! + 1) << (SLOT_BITS * i)
  return p
}

/** All ordered sequences of ≤ MAX_MARKS distinct cells: 1 + 9 + 72 + 504 = 586. */
function enumerateSides(): number[] {
  const out: number[] = []
  const walk = (cells: Cell[], used: number) => {
    out.push(packSide(cells))
    if (cells.length === MAX_MARKS) return
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (used & (1 << c)) continue
      walk([...cells, c as Cell], used | (1 << c))
    }
  }
  walk([], 0)
  return out
}

const SIDE_PACKED = Uint16Array.from(enumerateSides())
const SIDE_COUNT_N = SIDE_PACKED.length
const SIDE_INDEX = new Int16Array(PACK_SPACE).fill(-1)
const SIDE_MASK = new Uint16Array(PACK_SPACE)
const SIDE_COUNT = new Uint8Array(PACK_SPACE)
for (let i = 0; i < SIDE_COUNT_N; i++) {
  const p = SIDE_PACKED[i]!
  SIDE_INDEX[p] = i
  let mask = 0
  let count = 0
  for (let s = 0; s < MAX_MARKS; s++) {
    const v = (p >> (SLOT_BITS * s)) & NIBBLE
    if (v === 0) break
    mask |= 1 << (v - 1)
    count++
  }
  SIDE_MASK[p] = mask
  SIDE_COUNT[p] = count
}
const WIN_MASKS = WIN_LINES.map((line) => {
  let m = 0
  for (const c of line) m |= 1 << c
  return m
})

/** Mirrors `applyMove` on the packed side: drop the oldest nibble when full, append the new cell. */
function place(p: number, cell: number): number {
  const full = SIDE_COUNT[p]! >= MAX_MARKS
  return full
    ? (p >> SLOT_BITS) | ((cell + 1) << (SLOT_BITS * (MAX_MARKS - 1)))
    : p | ((cell + 1) << (SLOT_BITS * SIDE_COUNT[p]!))
}

function hasLine(p: number): boolean {
  const mask = SIDE_MASK[p]!
  for (const m of WIN_MASKS) if ((mask & m) === m) return true
  return false
}

const SLOT_COUNT = SIDE_COUNT_N * SIDE_COUNT_N * 2

function slotOf(xi: number, oi: number, oToMove: boolean): number {
  return (xi * SIDE_COUNT_N + oi) * 2 + (oToMove ? 1 : 0)
}

// ---- table ------------------------------------------------------------------------------------

export interface XoxTable {
  /** Per slot: 0 = cycle (or unreachable), +d = side to move wins in d plies, -(d+1) = loses in d plies. */
  readonly codes: Int8Array
  /** 1 for every position reachable from the empty board. */
  readonly reached: Uint8Array
  /** Reachable positions (128,170). */
  readonly size: number
  /** Milliseconds spent building. */
  readonly buildMs: number
}

export function buildXoxTable(): XoxTable {
  const t0 = Date.now()
  const codes = new Int8Array(SLOT_COUNT)
  const reached = new Uint8Array(SLOT_COUNT)
  const pending = new Uint8Array(SLOT_COUNT) // unresolved successors per node
  const bfs = new Int32Array(SLOT_COUNT)
  const resolve = new Int32Array(SLOT_COUNT)
  const edgeChild = new Int32Array(SLOT_COUNT)
  const edgeParent = new Int32Array(SLOT_COUNT)
  let edges = 0
  let size = 0
  let head = 0
  let tail = 0
  let rHead = 0
  let rTail = 0

  const root = slotOf(SIDE_INDEX[0]!, SIDE_INDEX[0]!, false)
  reached[root] = 1
  size++
  bfs[tail++] = root

  while (head < tail) {
    const node = bfs[head++]!
    const oToMove = (node & 1) === 1
    const pair = node >> 1
    const xi = Math.floor(pair / SIDE_COUNT_N)
    const oi = pair % SIDE_COUNT_N
    const xp = SIDE_PACKED[xi]!
    const op = SIDE_PACKED[oi]!
    const occupied = SIDE_MASK[xp]! | SIDE_MASK[op]!
    const moverP = oToMove ? op : xp
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (occupied & (1 << c)) continue
      const np = place(moverP, c)
      const ni = SIDE_INDEX[np]!
      const child = oToMove ? slotOf(xi, ni, false) : slotOf(ni, oi, true)
      if (edges >= SLOT_COUNT) throw new Error('xox solver: edge buffer overflow')
      edgeChild[edges] = child
      edgeParent[edges] = node
      edges++
      pending[node]!++
      if (reached[child]) continue
      reached[child] = 1
      size++
      if (hasLine(np)) {
        codes[child] = -1 // the side to move has just lost; distance 0
        resolve[rTail++] = child
      } else {
        bfs[tail++] = child
      }
    }
  }

  // Predecessor lists in CSR form.
  const offsets = new Int32Array(SLOT_COUNT + 1)
  for (let e = 0; e < edges; e++) offsets[edgeChild[e]! + 1]!++
  for (let i = 0; i < SLOT_COUNT; i++) offsets[i + 1]! += offsets[i]!
  const fill = offsets.slice(0, SLOT_COUNT)
  const preds = new Int32Array(edges)
  for (let e = 0; e < edges; e++) preds[fill[edgeChild[e]!]!++] = edgeParent[e]!

  // Retrograde: FIFO keeps distances non-decreasing, so the first assignment is the true one.
  while (rHead < rTail) {
    const n = resolve[rHead++]!
    const code = codes[n]!
    const loss = code < 0
    const d = loss ? -code - 1 : code
    for (let k = offsets[n]!; k < offsets[n + 1]!; k++) {
      const p = preds[k]!
      if (codes[p] !== 0) continue
      if (loss) {
        codes[p] = d + 1 // a reply that leaves the opponent lost: we win
        resolve[rTail++] = p
      } else if (--pending[p]! === 0) {
        codes[p] = -(d + 2) // every reply hands the opponent a win: we lose in d+1
        resolve[rTail++] = p
      }
    }
  }

  return { codes, reached, size, buildMs: Date.now() - t0 }
}

let singleton: XoxTable | null = null
/** Built on first use; the server calls this once at startup so no player pays for it. */
export function xoxTable(): XoxTable {
  singleton ??= buildXoxTable()
  return singleton
}

// ---- lookups ----------------------------------------------------------------------------------

export type PositionValue = 'win' | 'loss' | 'cycle'
export interface Evaluation {
  /** From the side to move's point of view. */
  value: PositionValue
  /** Plies to the end under perfect play; 0 for cycles. */
  distance: number
}

/**
 * Identity of a position for repetition checks: ordered marks + side to move. Shared by server
 * and solver so both agree on what "the same position" means.
 */
export function positionKey(state: GameState): string {
  return `${state.marks.X.join('')}|${state.marks.O.join('')}|${state.turn}`
}

/** The table is built for X-first games; an O-first game is the same game with colours swapped. */
function slotOfState(state: GameState): number {
  const first = state.moveCount % 2 === 0 ? state.turn : other(state.turn)
  const [a, b] = first === 'X' ? [state.marks.X, state.marks.O] : [state.marks.O, state.marks.X]
  const oToMove = state.turn !== first
  return slotOf(SIDE_INDEX[packSide(a)]!, SIDE_INDEX[packSide(b)]!, oToMove)
}

export function evaluate(table: XoxTable, state: GameState): Evaluation {
  const slot = slotOfState(state)
  if (!table.reached[slot])
    throw new Error('xox solver: position is not reachable from an empty board')
  const code = table.codes[slot]!
  if (code > 0) return { value: 'win', distance: code }
  if (code < 0) return { value: 'loss', distance: -code - 1 }
  return { value: 'cycle', distance: 0 }
}

// ---- move policy ------------------------------------------------------------------------------

export type OpeningPolicy = 'draw-holding' | 'forced-win'

export interface BestMoveOptions {
  /** First move of a game only. `draw-holding` (default) refuses the forced-win edge openings. */
  opening?: OpeningPolicy
  /** Source of [0,1) numbers for tie-breaks; omit for the lowest cell (fully deterministic). */
  random?: () => number
  /** Position keys seen so far in this game; cycle play avoids repeating one when it can. */
  history?: readonly string[]
}

interface Candidate {
  cell: Cell
  /** 0 = we win, 1 = cycle, 2 = we lose. */
  klass: 0 | 1 | 2
  /** Lower is better within a class. */
  score: number
}

/**
 * Perfect move for the side to move. Never chooses a losing move when a non-losing one exists;
 * wins as fast as possible, loses as slowly as possible, and in cycle positions picks the move
 * that leaves the opponent the fewest safe replies (the best odds a human slips).
 */
export function bestMove(table: XoxTable, state: GameState, opts: BestMoveOptions = {}): Cell {
  if (state.status !== 'playing') throw new Error('xox solver: game is over')
  const history = new Set(opts.history ?? [])
  const candidates: Candidate[] = []
  for (let c = 0; c < BOARD_SIZE; c++) {
    const r = applyMove(state, state.turn, c)
    if (!r.ok) continue
    const cell = c as Cell
    if (r.state.status !== 'playing') {
      candidates.push({ cell, klass: 0, score: 1 })
      continue
    }
    const ev = evaluate(table, r.state) // opponent to move
    if (ev.value === 'loss') candidates.push({ cell, klass: 0, score: ev.distance + 1 })
    else if (ev.value === 'win') candidates.push({ cell, klass: 2, score: -(ev.distance + 1) })
    else {
      const repeats = history.has(positionKey(r.state)) ? 100 : 0
      candidates.push({ cell, klass: 1, score: safeReplies(table, r.state) + repeats })
    }
  }
  if (candidates.length === 0) throw new Error('xox solver: no legal move')

  let pool = candidates
  if (state.moveCount === 0 && (opts.opening ?? 'draw-holding') === 'draw-holding') {
    const holding = pool.filter((k) => k.klass === 1)
    if (holding.length > 0) pool = holding
  }
  const bestClass = Math.min(...pool.map((k) => k.klass))
  pool = pool.filter((k) => k.klass === bestClass)
  const bestScore = Math.min(...pool.map((k) => k.score))
  pool = pool.filter((k) => k.score === bestScore).sort((a, b) => a.cell - b.cell)
  const pick = opts.random ? Math.floor(opts.random() * pool.length) : 0
  return pool[Math.min(pick, pool.length - 1)]!.cell
}

/** How many replies leave the opponent not lost (fewer = more chances for them to err). */
function safeReplies(table: XoxTable, state: GameState): number {
  let n = 0
  for (let c = 0; c < BOARD_SIZE; c++) {
    const r = applyMove(state, state.turn, c)
    if (!r.ok) continue
    if (r.state.status !== 'playing' || evaluate(table, r.state).value !== 'win') n++
  }
  return n
}

// ---- draw rules for the computer mode ---------------------------------------------------------

export const REPETITION_DRAW_COUNT = 3
export const AI_DRAW_PLY_CAP = 60

/**
 * `keys` holds the position key before every move plus the current one (so `keys.length - 1`
 * moves have been played). Returns why the game is a draw, or null while it goes on.
 */
export function isAiDraw(
  keys: readonly string[],
  plyCap = AI_DRAW_PLY_CAP,
): 'repetition' | 'cap' | null {
  const last = keys[keys.length - 1]
  if (last === undefined) return null
  let seen = 0
  for (const k of keys) if (k === last) seen++
  if (seen >= REPETITION_DRAW_COUNT) return 'repetition'
  if (keys.length - 1 >= plyCap) return 'cap'
  return null
}

export { seededRandom }

/** Convenience for tests and tooling: the empty-board position key. */
export const START_KEY = positionKey(createGame('X'))
